// What a catalogue row says about ONE model — may this key run it, what window
// did the provider report, what does it charge. See `../llm-credential.ts` for
// the file docblock.

import {
  type BYOKKey,
  type BYOKKeyModel,
  type BYOKTier,
  BYOKTierSchema,
  type ModelLimitsSource,
  modelLive,
  plausibleContextLength,
  plausibleMaxOutputTokens,
  plausibleTokenRate,
} from '../../entities/byok';
import { activeModels, modelActive } from './predicates';

/**
 * Why a key cannot address a model a resolution named — the TYPED REFUSAL that
 * replaces a provider 404 nobody downstream can read (arc 101 slice 02 step 7).
 *
 * The drift this exists for: a provider RETIRES a model id after the key was
 * verified. `verifyCapturedKey` (`@developerz/billing`) short-circuits a `valid`
 * row on purpose — for every key that still WORKS, which is the whole population
 * until one is parked shut — and {@link classifyLlmFailure} grades only 402/429, so
 * the run's 404 cools no key, rotates no key, and reaches the maintainer as a bare
 * "failed".
 *
 * The remedy is NOT an UNBOUNDED periodic re-probe: a cron calling every customer's
 * provider on a timer, on the customer's money, is the 2026-08-12 incident rebuilt
 * (three keys re-probed every 2 minutes, each pass a REAL paid `generateObject`,
 * until a human revoked them — `packages/billing/src/byok.ts`). What #4366 added is
 * the bounded opposite, and only for a key that can serve NOTHING: a key parked
 * shut (`valid`, its own `byok_keys.model` parked) is stamped 24h out by the park
 * itself, re-probed by `cron.byok_reverify`'s parked-shut arm, and given up on after
 * `MODEL_PARK_MAX_CONSECUTIVE_PARKS` (3) consecutive parks, after which the key is
 * the owner's. A key with a live catalogue is claimed by nothing and still
 * short-circuits. The list does NOT refresh when a human opens the key — `GET
 * /v1/billing/keys/:id/models` is a read that writes no row — so resolution's job is
 * unchanged whatever the clock does: REFUSE, by name, what the catalogue says the
 * credential cannot address.
 *
 * TWO members, each a fact the catalogue actually states (the bar for a third).
 * `byok_key_models` is a TAG SET, not an allowlist — every pre-slice key carries
 * exactly one backfilled row (migration 0165) — so "absent from the catalogue"
 * says nothing about what the provider serves, and refusing it would refuse every
 * legitimate `llm_routes` pin in production.
 * A PARKED row is the opposite: a VERIFY probed this key for this model and the
 * model could not answer, so `markKeyValidParkingModel` (`@developerz/db`) turned
 * the row off — a machine's verdict, never a person's. That is a positive
 * statement.
 *
 * COOLING (2026-10-17) is the second: the named model's `coolingUntil` is ahead AND no
 * other row on the key is live — every model the key serves is waiting out a dated
 * refusal, so the key serves nothing until the earliest reset. The SQL twin is
 * `modelParkFence`'s cooling arm (`@developerz/db`); a cooling model WITH a live sibling
 * is no refusal — `resolveKeyModel` steps to the sibling.
 */
export const LlmModelRefusal = {
  /** The catalogue names this model and it is PARKED (`byok_key_models.active = false`). */
  Parked: 'model_parked',
  /** The named model is cooling and NOTHING else on the key is live (`coolingUntil`). */
  Cooling: 'model_cooling',
} as const;
export type LlmModelRefusal = (typeof LlmModelRefusal)[keyof typeof LlmModelRefusal];

/**
 * Grade a candidate model against a key's catalogue: a named {@link
 * LlmModelRefusal}, or `null` when nothing the key has told us contradicts it.
 *
 * Exported because the WORKER's model ladder (`apps/worker/src/dispatch/llm-resolver.ts`)
 * names models this resolver never graded — a route pin sealed on a key matched by
 * PROVIDER, a member's stored default — and
 * one grader in one place is what keeps the two halves of the decision from
 * disagreeing about which models a credential can address.
 */
export function refuseKeyModel(
  key: Pick<BYOKKey, 'models'>,
  model: string,
  now: Date = new Date(),
): LlmModelRefusal | null {
  // Ids are unique within a catalogue (`CapturedKeyModelsSchema` + the
  // `(key_id, model)` unique index), so the first match is the only match.
  const entry = (key.models ?? []).find((m) => m.model === model);
  if (entry === undefined) return null;
  if (!modelActive(entry)) return LlmModelRefusal.Parked;
  // `entry` itself is not live here, so "some row live" is "some OTHER row live".
  const unserved = !modelLive(entry, now) && !(key.models ?? []).some((m) => modelLive(m, now));
  return unserved ? LlmModelRefusal.Cooling : null;
}

/**
 * The PROVIDER-REPORTED context window this key's catalogue carries for `model`,
 * or `undefined` when it carries none it will act on (arc 101 slice 02).
 *
 * The other half of the fact `refuseKeyModel` reads off the same row, and it sits
 * beside it for that reason: "what does this credential say about this model" has
 * one lookup, so a second consumer cannot re-derive the entry match differently.
 *
 * `plausibleContextLength` is applied HERE rather than trusted from the row.
 * `byok_key_models` is reachable by raw INSERT (the `router` pool rows in
 * `byok_keys` are written exactly that way, #1490), and the whole reason this
 * column exists is that a WRONG window is worse than no window — it fails
 * silently, in tokens the customer paid for. Undefined therefore means "fall back
 * to what you did before", never "zero".
 *
 * A PARKED entry still answers: the window is a fact about the model, not a
 * permission to use it, and the caller that sizes a context is not the caller that
 * decides whether the model may run. Refusal has its own grader.
 */
export function detectedContextLength(
  key: Pick<BYOKKey, 'models'>,
  model: string,
): number | undefined {
  const entry = (key.models ?? []).find((m) => m.model === model);
  return plausibleContextLength(entry?.context_length);
}

/**
 * The DETECTED limits of one model — exactly the `detected` input
 * `modelLimits` (`@developerz/agent`) resolves first (#4240). Each number carries
 * ITS OWN source (migration 0334): a number is non-null only when its source is.
 */
export type DetectedModelLimits = {
  readonly contextLength: number | null;
  readonly contextLengthSource: ModelLimitsSource | null;
  readonly maxOutputTokens: number | null;
  readonly maxOutputTokensSource: ModelLimitsSource | null;
};

/**
 * What this key's catalogue DETECTED about `model`, or `null` when nothing was —
 * no row, or a row with neither source set (never detected; a capture-supplied
 * window is not a detection and does not answer here).
 *
 * PER NUMBER: a number whose own source is null reads `null` (a captured window
 * beside a learned output ceiling is still not a detected window), and so does a
 * number graded away at the read (`plausibleContextLength`,
 * `plausibleMaxOutputTokens`) for the reason {@link detectedContextLength} gives —
 * the table is reachable by raw INSERT — while its source still answers, so the
 * resolver can fall through per field. The by-id read in `@developerz/db`
 * (`queries/byok/limits.ts`) answers through this function, so the attached catalogue
 * and the by-id read are one rule.
 */
export function detectedModelLimits(
  key: Pick<BYOKKey, 'models'>,
  model: string,
): DetectedModelLimits | null {
  const entry = (key.models ?? []).find((m) => m.model === model);
  const contextLengthSource = entry?.context_length_source ?? null;
  const maxOutputTokensSource = entry?.max_output_tokens_source ?? null;
  if (contextLengthSource === null && maxOutputTokensSource === null) return null;
  return {
    contextLength:
      contextLengthSource === null ? null : (plausibleContextLength(entry?.context_length) ?? null),
    contextLengthSource,
    maxOutputTokens:
      maxOutputTokensSource === null
        ? null
        : (plausibleMaxOutputTokens(entry?.max_output_tokens) ?? null),
    maxOutputTokensSource,
  };
}

/**
 * What a run on this model COSTS PER 1M TOKENS according to the provider itself —
 * both legs, or nothing.
 *
 * BOTH OR NOTHING is the contract, and it is why this returns a pair rather than
 * two nullable numbers. A rate for input with none for output prices half a run;
 * a caller that took the half it got would produce a figure that is neither the
 * provider's price nor the registry's, sourced to neither, which is exactly the
 * untraceable number `PriceBasis` exists to make unrepresentable.
 *
 * The third sibling of `refuseKeyModel` and {@link detectedContextLength}, reading
 * the same catalogue row through the same entry match, so "what does this
 * credential say about this model" keeps one lookup and three answers instead of
 * three lookups that can disagree about which row matched.
 *
 * `plausibleTokenRate` is applied HERE rather than trusted from the row, for the
 * reason its sibling gives: `byok_key_models` is reachable by raw INSERT (the
 * `router` pool rows in `byok_keys` are written exactly that way, #1490), and the
 * value steers a dollar figure a customer reads. Undefined means "fall back to the
 * declared tables", never "free".
 *
 * A PARKED entry still answers. A price is a fact about the model, not a
 * permission to run it, and the caller pricing tokens already spent is not the
 * caller deciding whether they may be spent.
 */
export function detectedModelRates(
  key: Pick<BYOKKey, 'models'>,
  model: string,
): { inputPerMtok: number; outputPerMtok: number } | undefined {
  const entry = (key.models ?? []).find((m) => m.model === model);
  const inputPerMtok = plausibleTokenRate(entry?.input_per_mtok);
  const outputPerMtok = plausibleTokenRate(entry?.output_per_mtok);
  return inputPerMtok === undefined || outputPerMtok === undefined
    ? undefined
    : { inputPerMtok, outputPerMtok };
}

/**
 * Can this key address `model` at all — its own {@link BYOKKey.model}, or a LIVE
 * entry of its tagged catalogue (arc 101 slice 02)?
 *
 * This is what widens a `model` pin from "the key's single model id" to "a model
 * this credential serves". Before the catalogue existed the two were the same
 * question, and after migration 0165 they still are for every backfilled key (one
 * untagged entry equal to `model`) — a pin only reaches further once a capture
 * actually tags a second model.
 *
 * A pin the key CAN serve both ranks it up ({@link matchScore}) and decides the
 * pair's model ({@link resolveKeyModel}) — one answer, so the resolver can never
 * rank a key FOR a model and then hand back a different one.
 *
 * A PARKED entry loses to nothing else here: it is checked FIRST and it also
 * refuses the key's own `model`. The two used to disagree — `key.model === model`
 * short-circuited ahead of the catalogue, so parking the row for a key's own model
 * did nothing at all, while the schema docblock promised "a parked model is
 * invisible to resolution, neither matched by a pin nor by a tier need". That is
 * the exact remedy a human has after a provider retires an id, so it is the one
 * that had to work.
 *
 * A COOLING entry (`coolingUntil` past `now`, 2026-10-17) is not served either, own
 * model included — but unlike a parked one it is no refusal: the key still serves its
 * other models, and the entry lapses back on its own.
 */
export function keyServesModel(key: BYOKKey, model: string, now: Date = new Date()): boolean {
  if (refuseKeyModel(key, model, now) !== null) return false;
  const entry = (key.models ?? []).find((m) => m.model === model);
  return entry !== undefined ? modelLive(entry, now) : key.model === model;
}

/**
 * The best LIVE catalogue entry for this tier chain, or `null` — lowest chain
 * index wins (primary tier over a fallback), ties broken by model id ascending.
 *
 * The tiebreak is a TOTAL ORDER ON THE DATA, deliberately not "the first row the
 * loader returned": several models at one tier is the shape this slice exists to
 * allow, so which one wins must not turn on row order, a re-listing, or a caller
 * that built the array by hand.
 */
export function bestTaggedModel(
  key: BYOKKey,
  tierChain: readonly BYOKTier[],
  now: Date = new Date(),
): { model: BYOKKeyModel; idx: number } | null {
  let best: { model: BYOKKeyModel; idx: number } | null = null;
  for (const m of activeModels(key, now)) {
    if (!m.tier) continue;
    const idx = tierChain.indexOf(m.tier);
    if (idx < 0) continue;
    if (best === null || idx < best.idx || (idx === best.idx && m.model < best.model.model)) {
      best = { model: m, idx };
    }
  }
  return best;
}

/**
 * The next LIVE catalogue model on this key when the one resolution would name is
 * cooling (2026-10-17) — closest tier to `tier` on the ladder first (`fast` → `heavy`),
 * then any untagged live model, ties by model id; `null` when nothing on the key is live.
 *
 * The rung that keeps a router key serving through ONE upstream's dated quota: the
 * whole key cools only when this answers `null` (`modelParkOf`, `../../entities/byok`).
 */
export function nextLiveModel(key: BYOKKey, tier: BYOKTier | undefined, now: Date): string | null {
  const ladder = BYOKTierSchema.options;
  // Untagged ranks past every tagged distance (the ladder's length is one beyond the widest).
  const distance = (m: BYOKKeyModel): number => {
    if (!m.tier) return ladder.length;
    return tier ? Math.abs(ladder.indexOf(m.tier) - ladder.indexOf(tier)) : 0;
  };
  let best: BYOKKeyModel | null = null;
  for (const m of activeModels(key, now)) {
    const d = best === null ? -1 : distance(m) - distance(best);
    if (d < 0 || (d === 0 && best !== null && m.model < best.model)) best = m;
  }
  return best?.model ?? null;
}

/**
 * Does this key's catalogue hold `model` COOLING at `now` — a live (not parked) row
 * whose `coolingUntil` is still ahead (2026-10-17)? The ONE check the ladders that
 * name a model the resolver never graded (a route pin matched by provider or label, a
 * member default) ask before overriding the resolver's live pair: unlike a parked row
 * it is no refusal, just a model that is not served until its reset.
 */
export function keyModelCooling(key: Pick<BYOKKey, 'models'>, model: string, now: Date): boolean {
  const entry = (key.models ?? []).find((m) => m.model === model);
  return entry !== undefined && modelActive(entry) && !modelLive(entry, now);
}

/** Which model a verify probe asks, or why it asks none — see {@link verifyProbeModelOf}. */
export type VerifyProbeModel =
  /** The key's own `model`: live, parked (the parked-shut re-probe asks it on purpose), or uncatalogued. */
  | { readonly kind: 'own'; readonly model: string }
  /** The own model is cooling; this LIVE sibling proves the credential instead. */
  | { readonly kind: 'live_sibling'; readonly model: string }
  /** The own model is cooling and nothing on the key is live: no probe until `until`. */
  | { readonly kind: 'all_cooling'; readonly until: Date };

/**
 * The model a VERIFY probe (`verifyCapturedKey`, `@developerz/billing`) asks
 * (2026-10-18). A probe proves the CREDENTIAL by asking one model; asking a model
 * whose catalogue row is cooling on a STATED reset asks for a known 429.
 *
 * Measured, tesote router key `78084c7f`: its own `model` (`gpt-6.1-sol`) was parked
 * until 2026-10-22 while `glm-5.3` was live. The key was cooled `rate_limited` at
 * 08:51Z (its last live model walled with the same reset), and every
 * `cron.byok_reverify` probe after the reset asked gpt, got the 429, graded it
 * `unreachable` and re-stamped the 30 s floor — 5.5 h of a dead pool, 12 idle boxes,
 * until a human restored the row. So: the own model unless it is cooling; then the
 * next LIVE model on the key, by the resolver's own rung ({@link nextLiveModel}), so
 * the probe asks what a session would run; and when nothing is live, no probe at all
 * before the earliest stated lapse.
 */
export function verifyProbeModelOf(key: BYOKKey, now: Date): VerifyProbeModel {
  if (!keyModelCooling(key, key.model, now)) return { kind: 'own', model: key.model };
  const live = nextLiveModel(key, key.tier ?? undefined, now);
  if (live !== null) return { kind: 'live_sibling', model: live };
  // Non-empty: the own row is active and cooling past `now`, so it is one of them.
  const lapses = (key.models ?? [])
    .filter((m) => modelActive(m) && m.coolingUntil && m.coolingUntil > now)
    .map((m) => (m.coolingUntil as Date).getTime());
  return { kind: 'all_cooling', until: new Date(Math.min(...lapses)) };
}
